import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import { CommonModule, DatePipe, isPlatformBrowser } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RouterModule } from '@angular/router';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  Unsubscribe,
  updateDoc,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { AuthService } from './auth.service';
import { Header } from './header';
import { PersonCard } from './person-card';
import { SwipeTabSyncDirective } from './directives/swipe-tab-sync.directive';
import { db, handleFirestoreError, OperationType, storage } from './firebase';
import { AccountStatus, AuthProfile } from './models/auth.model';
import { NotificationService } from './notification';

export type ComplaintTab = 'All' | 'Pending' | 'Closed' | 'all' | 'pending' | 'closed';
export type ComplaintStatus = 'pending' | 'closed';
export type ComplaintCategory =
  | 'account'
  | 'verification'
  | 'payments'
  | 'technical'
  | 'privacy'
  | 'safety'
  | 'other';
export type ComplaintPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface EverComplaint {
  id: string;
  uid: string;
  ticketNumber: string;
  subject: string;
  category: ComplaintCategory;
  priority: ComplaintPriority;
  description: string;
  status: ComplaintStatus;
  attachmentUrl?: string;
  resolutionNote?: string;
  adminReply?: string;
  adminRepliedBy?: string;
  adminRepliedAt?: Timestamp | string | Date | null;
  userDisplayName?: string;
  userUsername?: string;
  userPhotoURL?: string;
  createdAt: Timestamp | string | Date;
  updatedAt: Timestamp | string | Date;
  closedAt?: Timestamp | string | Date | null;
}

export interface EverComplaintUpdate {
  id: string;
  uid: string;
  authorName: string;
  authorRole?: 'admin' | 'user';
  message: string;
  createdAt: Timestamp | string | Date;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-ever',
  imports: [
    CommonModule,
    MatIconModule,
    RouterModule,
    ReactiveFormsModule,
    Header,
    PersonCard,
    SwipeTabSyncDirective,
  ],
  providers: [DatePipe],
  styles: [
    `
      @reference "../styles.css";
      .profile-header-card {
        @apply bg-white/40 dark:bg-zinc-900/40 backdrop-blur-3xl border border-white/20 dark:border-white/10 rounded-[48px] overflow-hidden shadow-ios relative;
      }
      .profile-header-card::before {
        content: '';
        @apply absolute inset-0 bg-gradient-to-tr from-ios-blue/10 via-transparent to-ios-purple/10 pointer-events-none opacity-50;
      }
      .metric-card {
        @apply bg-black/5 dark:bg-white/5 rounded-3xl p-5 sm:p-6 flex flex-col items-center justify-center transition-all hover:scale-[1.02] active:scale-[0.98] border border-black/5 dark:border-white/5 relative overflow-hidden cursor-pointer;
      }
      .metric-value {
        @apply text-2xl sm:text-3xl font-black tracking-tighter leading-none relative z-10 lowercase;
      }
      .bento-card {
        @apply bg-white/50 dark:bg-zinc-900/50 backdrop-blur-3xl border border-black/5 dark:border-white/5 rounded-[40px] sm:rounded-[48px] shadow-ios-md overflow-hidden transition-all relative;
      }
      .bento-card-liquid {
        @apply absolute -right-20 -top-20 w-48 h-48 rounded-full blur-[60px] pointer-events-none;
        animation: liquid-pulse 4s infinite ease-in-out;
      }
      .ios-list-group {
        @apply bg-white/60 dark:bg-zinc-900/60 backdrop-blur-2xl rounded-[32px] border border-black/5 dark:border-white/10 overflow-hidden shadow-ios-sm;
      }
      .ios-list-item {
        @apply flex items-center justify-between w-full px-6 transition-all hover:bg-black/5 dark:hover:bg-white/5 border-b border-black/5 dark:border-white/5 last:border-0;
      }
      .section-header {
        @apply px-4 sm:px-6 flex items-center justify-between gap-3 mb-4;
      }
      .section-title {
        @apply text-[10px] font-black uppercase tracking-[0.35em] opacity-40;
      }
    `,
  ],
  template: `
    <div class="min-h-screen w-full transition-colors duration-500 relative overflow-x-hidden flex flex-col bg-transparent">
      <!-- Header -->
      <app-header
        title="Ever"
        subtitle="@ever"
        iconName="ever"
        [backLink]="authService.isAuthenticated() ? '/search' : '/'"
      >
        @if (authService.isAuthenticated()) {
          <div actions class="relative z-10 flex items-center gap-2 shrink-0">
            <button
              type="button"
              (click)="openCreateModal()"
              class="h-9 sm:h-10 px-3.5 sm:px-4 rounded-full bg-ios-blue hover:bg-ios-blue/90 text-white border border-white/20 backdrop-blur-xl transition-all active:scale-95 flex items-center gap-1.5 text-xs font-bold shadow-md shadow-ios-blue/25 cursor-pointer"
              title="Add New Complaint"
              aria-label="Add New Complaint"
            >
              <mat-icon class="!text-[18px]">add</mat-icon>
              <span class="hidden sm:inline lowercase">new complaint</span>
            </button>
          </div>
        }
      </app-header>

      <!-- ========================================================================= -->
      <!-- STATE 1: LOGGED-IN VIEW — ADVANCED COMPLAINT MANAGEMENT SYSTEM (PROFILE UI) -->
      <!-- ========================================================================= -->
      @if (authService.isAuthenticated()) {
        <!-- Sub-Header for Status Tracking Chip Tabs ('All', 'Pending', 'Closed') -->
        <div class="ios-sub-header-liquid">
          <div class="chip-scroll-container justify-start sm:justify-center" #everChips>
            @for (chip of chips; track chip.label) {
              <button
                type="button"
                (click)="selectTab(chip.label)"
                class="ios-liquid-chip"
                [class.active]="selectedTab() === chip.label || selectedTab().toLowerCase() === chip.label.toLowerCase()"
                [id]="'tab-' + chip.label"
                [title]="'Filter by ' + chip.label"
              >
                <mat-icon class="text-[18px] transition-transform group-active:scale-110">
                  {{ chip.icon }}
                </mat-icon>
                <span class="tracking-tight">{{ chip.label }}</span>
                <span
                  class="text-[10px] px-2 py-0.5 rounded-full font-black leading-none transition-colors"
                  [class.bg-white/25]="selectedTab() === chip.label || selectedTab().toLowerCase() === chip.label.toLowerCase()"
                  [class.text-white]="selectedTab() === chip.label || selectedTab().toLowerCase() === chip.label.toLowerCase()"
                  [class.bg-black/10]="selectedTab() !== chip.label && selectedTab().toLowerCase() !== chip.label.toLowerCase()"
                  [class.dark:bg-white/10]="selectedTab() !== chip.label && selectedTab().toLowerCase() !== chip.label.toLowerCase()"
                >
                  {{ getCountForTab(chip.label) }}
                </span>
              </button>
            }
          </div>
        </div>

        <!-- Main Content Area Mimicking Profile Page Layout -->
        <main
          class="flex-1 pt-[132px] md:pt-[148px] pb-16 px-4 md:px-8 lg:px-12 max-w-7xl mx-auto w-full animate-ios-fade relative z-10"
          appSwipeTabSync
          [tabs]="chips"
          [activeTab]="selectedTab()"
          [chipContainer]="everChips"
          chipIdPrefix="tab-"
          (activeTabChange)="onTabSwipe($event)"
        >
          <div class="max-w-4xl mx-auto space-y-8">
            <!-- Profile-Mimicking Atmospheric Header & Metrics Background Card -->
            <div class="profile-header-card animate-ios-slide group overflow-hidden">
              <!-- Atmospheric Liquid Mesh Header -->
              <div class="h-auto md:h-48 bg-transparent relative flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 w-full p-6 md:p-8 overflow-hidden">
                <div class="absolute inset-0 bg-white/10 dark:bg-black/20 backdrop-blur-md"></div>
                <div class="absolute top-[-50%] left-[-20%] w-[140%] h-[200%] bg-ios-blue/50 blur-[80px] rounded-full animate-liquid"></div>
                <div
                  class="absolute bottom-[-20%] right-[-10%] w-[100%] h-[150%] bg-ios-purple/50 blur-[80px] rounded-full animate-liquid-pulse"
                  style="animation-duration: 8s"
                ></div>
                <div class="absolute inset-0 backdrop-blur-[6px] bg-black/10"></div>

                <!-- User Identity Block -->
                <div class="relative z-10 flex items-center gap-4 min-w-0">
                  <div class="relative w-20 h-20 md:w-24 md:h-24 shrink-0 transform group-hover:scale-105 transition-transform duration-700">
                    <div class="absolute inset-0 bg-gradient-to-tr from-ios-blue to-ios-purple opacity-70 blur-xl rounded-full pointer-events-none"></div>
                    <div class="relative w-full h-full bg-white/10 backdrop-blur-2xl p-1 shadow-2xl border border-white/20 rounded-full">
                      <div class="w-full h-full overflow-hidden bg-white/10 rounded-full relative">
                        @if (authService.userProfile()?.photoURL) {
                          <img
                            [src]="authService.userProfile()?.photoURL"
                            alt="User Profile"
                            class="w-full h-full object-cover rounded-full"
                            referrerpolicy="no-referrer"
                          />
                        } @else {
                          <div class="w-full h-full flex items-center justify-center text-2xl font-black text-white bg-gradient-to-tr from-ios-blue to-purple-500">
                            {{ (authService.userProfile()?.displayName || 'U').charAt(0).toUpperCase() }}
                          </div>
                        }
                      </div>
                    </div>
                  </div>

                  <div class="flex flex-col min-w-0 justify-center">
                    <h2 class="text-white font-bold text-lg sm:text-2xl leading-tight truncate drop-shadow-md">
                      {{ authService.userProfile()?.displayName || 'Authenticated User' }}
                    </h2>
                    <p class="text-white/85 font-medium text-xs sm:text-sm lowercase drop-shadow-sm mt-0.5">
                      &#64;{{ authService.userProfile()?.username || 'user' }}
                    </p>
                  </div>
                </div>

                <!-- Floating Plus Button Inside Hero Header -->
                <div class="relative z-10 flex items-center gap-2.5 self-end sm:self-center">
                  <button
                    type="button"
                    (click)="openCreateModal()"
                    class="h-12 px-5 rounded-2xl bg-white text-ios-blue hover:bg-white/90 font-extrabold text-xs sm:text-sm shadow-xl transition-all hover:scale-105 active:scale-95 flex items-center gap-2 cursor-pointer"
                    title="Submit New Complaint"
                  >
                    <mat-icon class="!text-[20px]">add_circle</mat-icon>
                    <span>New Complaint</span>
                  </button>
                </div>
              </div>

              <!-- Profile-Style Metric Cards & Quick Controls Bar -->
              <div class="p-6 sm:p-8 bg-white/50 dark:bg-zinc-900/50 backdrop-blur-3xl space-y-6">
                <!-- 3 Metric Cards for 'All', 'Pending', 'Closed' -->
                <div class="grid grid-cols-3 gap-3 sm:gap-5">
                  <button
                    type="button"
                    (click)="selectTab('All')"
                    class="metric-card group"
                    [class.ring-2]="selectedTab().toLowerCase() === 'all'"
                    [class.ring-ios-blue]="selectedTab().toLowerCase() === 'all'"
                  >
                    <span class="metric-value text-ios-blue drop-shadow-sm group-hover:scale-105 transition-transform">
                      {{ allCount() }}
                    </span>
                    <span class="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] opacity-75 mt-2">
                      All
                    </span>
                  </button>

                  <button
                    type="button"
                    (click)="selectTab('Pending')"
                    class="metric-card group"
                    [class.ring-2]="selectedTab().toLowerCase() === 'pending'"
                    [class.ring-amber-500]="selectedTab().toLowerCase() === 'pending'"
                  >
                    <span class="metric-value text-amber-500 drop-shadow-sm group-hover:scale-105 transition-transform">
                      {{ pendingCount() }}
                    </span>
                    <span class="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] opacity-75 mt-2">
                      Pending
                    </span>
                  </button>

                  <button
                    type="button"
                    (click)="selectTab('Closed')"
                    class="metric-card group"
                    [class.ring-2]="selectedTab().toLowerCase() === 'closed'"
                    [class.ring-emerald-500]="selectedTab().toLowerCase() === 'closed'"
                  >
                    <span class="metric-value text-emerald-500 drop-shadow-sm group-hover:scale-105 transition-transform">
                      {{ closedCount() }}
                    </span>
                    <span class="text-[10px] sm:text-[11px] font-black uppercase tracking-[0.2em] opacity-75 mt-2">
                      Closed
                    </span>
                  </button>
                </div>

                <!-- Search & Category Filter Row -->
                <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <div class="ios-input-container !h-12 flex-1">
                    <mat-icon class="text-ios-blue mr-2.5 !text-[20px]">search</mat-icon>
                    <input
                      type="text"
                      [formControl]="complaintSearchControl"
                      placeholder="Search complaints by ticket #, subject, or keyword..."
                      class="ios-input !text-sm"
                    />
                    @if (complaintSearchControl.value) {
                      <button
                        type="button"
                        (click)="complaintSearchControl.setValue('')"
                        class="w-7 h-7 rounded-full hover:bg-black/5 dark:hover:bg-white/10 flex items-center justify-center text-zinc-400 cursor-pointer"
                        title="Clear search"
                      >
                        <mat-icon class="!text-[16px]">close</mat-icon>
                      </button>
                    }
                  </div>

                  <div class="flex items-center gap-2">
                    <div class="ios-input-container !h-12 !w-auto min-w-[150px]">
                      <mat-icon class="text-ios-purple mr-2 !text-[18px]">category</mat-icon>
                      <select
                        [formControl]="categoryFilterControl"
                        class="ios-input !text-xs font-bold uppercase tracking-wider bg-transparent cursor-pointer"
                        aria-label="Filter by category"
                      >
                        <option value="all" class="bg-white dark:bg-zinc-900">All Categories</option>
                        <option value="account" class="bg-white dark:bg-zinc-900">Account</option>
                        <option value="verification" class="bg-white dark:bg-zinc-900">Verification</option>
                        <option value="payments" class="bg-white dark:bg-zinc-900">Payments</option>
                        <option value="technical" class="bg-white dark:bg-zinc-900">Technical</option>
                        <option value="privacy" class="bg-white dark:bg-zinc-900">Privacy</option>
                        <option value="safety" class="bg-white dark:bg-zinc-900">Safety</option>
                        <option value="other" class="bg-white dark:bg-zinc-900">Other</option>
                      </select>
                    </div>

                    <button
                      type="button"
                      (click)="openCreateModal()"
                      class="h-12 w-12 rounded-2xl bg-ios-blue text-white flex items-center justify-center shadow-ios shadow-ios-blue/25 hover:scale-105 active:scale-95 transition-all shrink-0 cursor-pointer"
                      title="Add New Complaint"
                      aria-label="Add New Complaint"
                    >
                      <mat-icon class="!text-[22px]">add</mat-icon>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Section Header with Plus Button Mimicking Profile Asset Tabs -->
            <div class="flex items-center justify-between px-2 sm:px-4">
              <div class="flex items-center gap-3">
                <div
                  class="w-1.5 h-6 rounded-full"
                  [class.bg-ios-blue]="selectedTab().toLowerCase() === 'all'"
                  [class.bg-amber-500]="selectedTab().toLowerCase() === 'pending'"
                  [class.bg-emerald-500]="selectedTab().toLowerCase() === 'closed'"
                ></div>
                <div>
                  <h3 class="text-xl font-bold tracking-tight">
                    {{ selectedTab() }} complaints
                  </h3>
                  <p class="text-[11px] font-medium opacity-50">
                    Showing {{ filteredComplaints().length }} record(s)
                  </p>
                </div>
              </div>

              <button
                type="button"
                (click)="openCreateModal()"
                class="h-11 px-5 rounded-2xl bg-ios-blue text-white font-bold text-xs sm:text-sm shadow-ios shadow-ios-blue/20 hover:scale-105 active:scale-95 transition-all flex items-center gap-2 cursor-pointer"
              >
                <mat-icon class="!text-[18px]">add</mat-icon>
                <span class="tracking-tight">Add Complaint</span>
              </button>
            </div>

            <!-- Loading State -->
            @if (isLoadingComplaints()) {
              <div class="flex flex-col items-center justify-center p-20 space-y-4">
                <div class="w-10 h-10 border-4 border-ios-blue/20 border-t-ios-blue rounded-full animate-spin"></div>
                <p class="text-xs font-black uppercase tracking-widest opacity-40">Syncing complaints...</p>
              </div>
            } @else if (filteredComplaints().length === 0) {
              <!-- Profile-Style Bento Empty State Card -->
              <div class="bento-card p-12 sm:p-16 flex flex-col items-center justify-center text-center animate-ios-slide relative overflow-hidden group">
                <div class="bento-card-liquid bg-ios-blue/20"></div>
                <div class="absolute inset-0 bg-gradient-to-tr from-ios-blue/5 via-transparent to-ios-purple/5"></div>
                <div class="relative z-10 w-24 h-24 rounded-3xl bg-white/60 dark:bg-zinc-800/60 backdrop-blur-md flex items-center justify-center mb-6 shadow-ios-sm border border-black/5 dark:border-white/10 group-hover:scale-110 transition-transform duration-700">
                  <mat-icon class="!text-5xl text-ios-blue opacity-60">
                    {{ selectedTab().toLowerCase() === 'closed' ? 'task_alt' : selectedTab().toLowerCase() === 'pending' ? 'pending_actions' : 'support_agent' }}
                  </mat-icon>
                </div>
                <h3 class="text-xl font-bold tracking-tight mb-2 relative z-10">
                  No {{ selectedTab().toLowerCase() === 'all' ? '' : selectedTab() + ' ' }}complaints found
                </h3>
                <p class="text-sm font-medium opacity-50 max-w-sm mb-8 relative z-10">
                  @if (selectedTab().toLowerCase() === 'pending') {
                    You have no pending complaints awaiting review right now.
                  } @else if (selectedTab().toLowerCase() === 'closed') {
                    Resolved and closed complaints will appear in this archive.
                  } @else {
                    Need assistance or want to report an issue? Click the plus button to submit a complaint directly to our support team.
                  }
                </p>
                <button
                  type="button"
                  (click)="openCreateModal()"
                  class="relative z-10 h-12 px-6 rounded-2xl bg-ios-blue text-white font-bold text-sm shadow-ios shadow-ios-blue/25 hover:scale-105 active:scale-95 transition-all flex items-center gap-2 cursor-pointer"
                >
                  <mat-icon class="!text-[20px]">add</mat-icon>
                  <span>Raise New Complaint</span>
                </button>
              </div>
            } @else {
              <!-- Complaint Cards Grid / Stack Mimicking Profile Bento Cards -->
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6 animate-ios-slide">
                @for (item of filteredComplaints(); track item.id) {
                  <div
                    (click)="openDetailModal(item)"
                    (keydown.enter)="openDetailModal(item)"
                    tabindex="0"
                    class="cursor-pointer bento-card overflow-hidden group hover:shadow-ios-lg flex flex-col transform hover:-translate-y-1 transition-all duration-500"
                  >
                    <div
                      class="bento-card-liquid"
                      [class.bg-amber-500/15]="item.status === 'pending'"
                      [class.bg-emerald-500/15]="item.status === 'closed'"
                    ></div>

                    @if (item.attachmentUrl) {
                      <div class="aspect-video relative overflow-hidden bg-black/5 border-b border-black/5 dark:border-white/5">
                        <img
                          [src]="item.attachmentUrl"
                          alt="Complaint attachment"
                          class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                          referrerpolicy="no-referrer"
                        />
                        <div class="absolute top-3 left-3 px-3 py-1 rounded-full bg-black/60 backdrop-blur-md text-white text-[10px] font-mono font-bold">
                          {{ item.ticketNumber }}
                        </div>
                      </div>
                    }

                    <div class="p-6 sm:p-7 flex-1 flex flex-col relative z-10 bg-white/50 dark:bg-zinc-900/50">
                      <!-- Top Badges Row -->
                      <div class="flex items-center justify-between gap-2 mb-4 flex-wrap">
                        <div class="flex items-center gap-2 flex-wrap">
                          <span class="px-2.5 py-1 rounded-lg bg-black/5 dark:bg-white/10 text-[10px] font-mono font-bold text-zinc-700 dark:text-zinc-300">
                            {{ item.ticketNumber }}
                          </span>
                          <span class="px-2.5 py-1 rounded-lg bg-ios-blue/10 text-ios-blue text-[10px] font-black uppercase tracking-widest border border-ios-blue/20">
                            {{ item.category }}
                          </span>
                          <span
                            class="px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border"
                            [class.bg-rose-500/10]="item.priority === 'urgent' || item.priority === 'high'"
                            [class.text-rose-500]="item.priority === 'urgent' || item.priority === 'high'"
                            [class.border-rose-500/20]="item.priority === 'urgent' || item.priority === 'high'"
                            [class.bg-amber-500/10]="item.priority === 'medium'"
                            [class.text-amber-500]="item.priority === 'medium'"
                            [class.border-amber-500/20]="item.priority === 'medium'"
                            [class.bg-zinc-500/10]="item.priority === 'low'"
                            [class.text-zinc-500]="item.priority === 'low'"
                            [class.border-zinc-500/20]="item.priority === 'low'"
                          >
                            {{ item.priority }}
                          </span>
                        </div>

                        <!-- Status Pill ('pending' or 'closed') -->
                        <span
                          class="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 border"
                          [class.bg-amber-500/15]="item.status === 'pending'"
                          [class.text-amber-600]="item.status === 'pending'"
                          [class.dark:text-amber-400]="item.status === 'pending'"
                          [class.border-amber-500/30]="item.status === 'pending'"
                          [class.bg-emerald-500/15]="item.status === 'closed'"
                          [class.text-emerald-600]="item.status === 'closed'"
                          [class.dark:text-emerald-400]="item.status === 'closed'"
                          [class.border-emerald-500/30]="item.status === 'closed'"
                        >
                          <span
                            class="w-1.5 h-1.5 rounded-full"
                            [class.bg-amber-500]="item.status === 'pending'"
                            [class.animate-pulse]="item.status === 'pending'"
                            [class.bg-emerald-500]="item.status === 'closed'"
                          ></span>
                          {{ item.status }}
                        </span>
                      </div>

                      <!-- Complaint Title & Description -->
                      <h4 class="font-bold text-lg sm:text-xl tracking-tight mb-2 group-hover:text-ios-blue transition-colors line-clamp-1">
                        {{ item.subject }}
                      </h4>
                      <p class="text-sm opacity-65 line-clamp-3 mb-5 font-medium leading-relaxed">
                        {{ item.description }}
                      </p>

                      @if (item.adminReply || item.resolutionNote) {
                        <div class="mb-5 p-3.5 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 text-xs">
                          <div class="font-black uppercase tracking-widest text-[9px] text-emerald-600 dark:text-emerald-400 mb-1 flex items-center gap-1">
                            <mat-icon class="!text-[13px]">verified</mat-icon>
                            <span>{{ item.adminReply ? 'Admin Reply' : 'Resolution Note' }}</span>
                          </div>
                          <p class="opacity-80 line-clamp-2">{{ item.adminReply || item.resolutionNote }}</p>
                        </div>
                      }

                      <!-- Footer Metadata & Quick Status Toggle -->
                      <div class="mt-auto pt-4 border-t border-black/5 dark:border-white/5 flex items-center justify-between gap-2">
                        <div class="flex items-center gap-2 text-[11px] font-bold opacity-45">
                          <mat-icon class="!text-[15px]">schedule</mat-icon>
                          <span>{{ toDate(item.createdAt) | date: 'MMM d, y • h:mm a' }}</span>
                        </div>

                        <div class="flex items-center gap-1.5" (click)="$event.stopPropagation()">
                          <button
                            type="button"
                            (click)="toggleComplaintStatus(item)"
                            class="h-8 px-3 rounded-xl text-[11px] font-bold transition-all active:scale-95 flex items-center gap-1 cursor-pointer border"
                            [class.bg-emerald-500/10]="item.status === 'pending'"
                            [class.text-emerald-600]="item.status === 'pending'"
                            [class.dark:text-emerald-400]="item.status === 'pending'"
                            [class.border-emerald-500/20]="item.status === 'pending'"
                            [class.hover:bg-emerald-500]="item.status === 'pending'"
                            [class.hover:text-white]="item.status === 'pending'"
                            [class.bg-amber-500/10]="item.status === 'closed'"
                            [class.text-amber-600]="item.status === 'closed'"
                            [class.dark:text-amber-400]="item.status === 'closed'"
                            [class.border-amber-500/20]="item.status === 'closed'"
                            [class.hover:bg-amber-500]="item.status === 'closed'"
                            [class.hover:text-white]="item.status === 'closed'"
                            [title]="item.status === 'pending' ? 'Mark as Closed' : 'Reopen as Pending'"
                          >
                            <mat-icon class="!text-[14px]">
                              {{ item.status === 'pending' ? 'check_circle' : 'replay' }}
                            </mat-icon>
                            <span>{{ item.status === 'pending' ? 'Close' : 'Reopen' }}</span>
                          </button>

                          <button
                            type="button"
                            (click)="deleteComplaint(item)"
                            class="w-8 h-8 rounded-xl bg-black/5 dark:bg-white/5 hover:bg-ios-red/15 hover:text-ios-red flex items-center justify-center transition-all active:scale-90 cursor-pointer"
                            title="Delete Complaint"
                            aria-label="Delete Complaint"
                          >
                            <mat-icon class="!text-[16px]">delete_outline</mat-icon>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                }
              </div>
            }

            <!-- Direct Support Channels Card (Profile-Style iOS List Group) -->
            <div class="space-y-4 pt-4">
              <div class="section-header">
                <div class="flex items-center gap-3">
                  <div class="w-1 h-4 bg-ios-purple/40 rounded-full"></div>
                  <h3 class="section-title">Direct Support Channels</h3>
                </div>
              </div>
              <div class="ios-list-group">
                <a href="mailto:support@kicgle.com" class="ios-list-item h-[72px] group">
                  <div class="flex items-center gap-4">
                    <div class="w-10 h-10 rounded-xl bg-ios-blue/10 flex items-center justify-center text-ios-blue shrink-0">
                      <mat-icon class="!text-[20px]">email</mat-icon>
                    </div>
                    <div class="flex flex-col">
                      <span class="text-[15px] font-bold tracking-tight">Email Support</span>
                      <span class="text-xs opacity-50">support&#64;kicgle.com</span>
                    </div>
                  </div>
                  <mat-icon class="text-ios-caption opacity-30 group-hover:translate-x-1 transition-transform">chevron_right</mat-icon>
                </a>
                <a href="tel:+918056774620" class="ios-list-item h-[72px] group">
                  <div class="flex items-center gap-4">
                    <div class="w-10 h-10 rounded-xl bg-ios-green/10 flex items-center justify-center text-ios-green shrink-0">
                      <mat-icon class="!text-[20px]">phone</mat-icon>
                    </div>
                    <div class="flex flex-col">
                      <span class="text-[15px] font-bold tracking-tight">Priority Helpline</span>
                      <span class="text-xs opacity-50">+91 8056774620 (24h SLA)</span>
                    </div>
                  </div>
                  <mat-icon class="text-ios-caption opacity-30 group-hover:translate-x-1 transition-transform">chevron_right</mat-icon>
                </a>
              </div>
            </div>
          </div>
        </main>
      } @else {
        <!-- ========================================================================= -->
        <!-- STATE 2: LOGGED-OUT VIEW — RETAINS EVER SUPPORT LAYOUT & CONTACT INFO     -->
        <!-- ========================================================================= -->
        <main class="flex-1 max-w-4xl mx-auto p-6 md:p-12 pt-[100px] md:pt-[120px] relative z-10 w-full animate-ios-fade">
          <div class="space-y-12">
            <!-- Search Ever -->
            <section class="text-center space-y-6 animate-ios-slide">
              <h2 class="text-ios-title text-3xl md:text-4xl font-bold">How can Ever assist you?</h2>
              <div class="relative max-w-xl mx-auto group">
                <div class="absolute inset-0 bg-ios-blue/10 blur-2xl rounded-full opacity-0 group-focus-within:opacity-100 transition-opacity duration-500"></div>
                <div class="relative flex items-center bg-light-surface/60 dark:bg-dark-surface/60 backdrop-blur-md border border-light-border dark:border-dark-border rounded-2xl px-5 py-4 shadow-lg focus-within:ring-2 focus-within:ring-ios-blue/30 transition-all">
                  <mat-icon class="text-ios-blue mr-3">search</mat-icon>
                  <input
                    type="text"
                    [formControl]="faqSearchControl"
                    placeholder="Search for support articles..."
                    class="bg-transparent border-none outline-none w-full text-ios-body placeholder:opacity-40"
                  />
                </div>
              </div>
            </section>

            <!-- FAQ Section -->
            <section class="space-y-6 animate-ios-slide" style="animation-delay: 0.1s">
              <h3 class="text-ios-headline text-xl flex items-center gap-3">
                <div class="w-10 h-10 rounded-xl bg-white/20 dark:bg-white/5 backdrop-blur-md border border-white/30 dark:border-white/10 overflow-hidden flex items-center justify-center shadow-md relative">
                  <div class="absolute inset-0">
                    <div class="absolute top-[-20%] left-[-10%] w-[120%] h-[150%] bg-ios-blue/30 blur-[10px] rounded-full animate-liquid"></div>
                    <div class="absolute bottom-[-10%] right-[-10%] w-[100%] h-[100%] bg-ios-purple/30 blur-[10px] rounded-full animate-liquid-pulse" style="animation-duration: 8s"></div>
                  </div>
                  <mat-icon class="text-ios-title dark:text-white text-[20px] relative z-10">support_agent</mat-icon>
                </div>
                Frequently Asked Questions
              </h3>
              <div class="space-y-4">
                @for (faq of filteredFaqs(); track faq.question) {
                  <div class="glass-card p-6 border-ios-blue/5 hover:border-ios-blue/20 transition-all group">
                    <h4 class="text-ios-headline text-lg mb-2 group-hover:text-ios-blue transition-colors">{{ faq.question }}</h4>
                    <p class="text-ios-body text-sm opacity-70 leading-relaxed">{{ faq.answer }}</p>
                  </div>
                }
              </div>
            </section>

            <!-- Contact Support -->
            <section class="animate-ios-slide" style="animation-delay: 0.2s">
              <h3 class="text-ios-headline text-xl flex items-center gap-3 mb-6">
                <mat-icon class="text-ios-purple">contact_support</mat-icon> Contact Support
              </h3>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <a href="mailto:support@kicgle.com" class="glass-card p-6 flex items-center gap-4 hover:bg-ios-blue/5 transition-all group">
                  <div class="w-12 h-12 rounded-2xl bg-ios-blue/10 text-ios-blue flex items-center justify-center group-hover:scale-110 transition-transform">
                    <mat-icon>email</mat-icon>
                  </div>
                  <div>
                    <h4 class="text-ios-headline">Email Support</h4>
                    <p class="text-ios-caption opacity-50">support&#64;kicgle.com</p>
                  </div>
                </a>
                <a href="tel:+918056774620" class="glass-card p-6 flex items-center gap-4 hover:bg-ios-blue/5 transition-all group">
                  <div class="w-12 h-12 rounded-2xl bg-ios-blue/10 text-ios-blue flex items-center justify-center group-hover:scale-110 transition-transform">
                    <mat-icon>phone</mat-icon>
                  </div>
                  <div>
                    <h4 class="text-ios-headline">Call Support</h4>
                    <p class="text-ios-caption opacity-50">+91 8056774620</p>
                  </div>
                </a>
                <div class="glass-card p-6 flex items-center gap-4 group">
                  <div class="w-12 h-12 rounded-2xl bg-ios-green/10 text-ios-green flex items-center justify-center group-hover:scale-110 transition-transform">
                    <mat-icon>schedule</mat-icon>
                  </div>
                  <div>
                    <h4 class="text-ios-headline">Response Time</h4>
                    <p class="text-ios-caption opacity-50">Within 24 hours</p>
                  </div>
                </div>
              </div>
            </section>
          </div>
        </main>
      }

      <!-- ========================================================================= -->
      <!-- MODAL 1: ADD NEW COMPLAINT MODAL                                          -->
      <!-- ========================================================================= -->
      @if (isCreateModalOpen()) {
        <div class="fixed inset-0 z-[1000] flex items-center justify-center p-4 animate-ios-fade">
          <div
            class="absolute inset-0 bg-black/60 backdrop-blur-md"
            (click)="closeCreateModal()"
            (keydown.enter)="closeCreateModal()"
            tabindex="0"
          ></div>

          <div class="relative w-full max-w-lg bg-white/95 dark:bg-zinc-900/95 backdrop-blur-3xl rounded-[36px] shadow-2xl border border-black/5 dark:border-white/10 animate-ios-slide overflow-hidden flex flex-col max-h-[90vh]">
            <!-- Modal Header -->
            <div class="p-6 border-b border-black/5 dark:border-white/10 flex items-center justify-between">
              <div class="flex items-center gap-3">
                <div class="w-10 h-10 rounded-2xl bg-ios-blue/10 text-ios-blue flex items-center justify-center">
                  <mat-icon>add_comment</mat-icon>
                </div>
                <div>
                  <h3 class="text-lg font-bold tracking-tight">Raise New Complaint</h3>
                  <p class="text-[11px] font-medium opacity-50">
                    Submit a support ticket to our team
                  </p>
                </div>
              </div>
              <button
                type="button"
                (click)="closeCreateModal()"
                class="w-9 h-9 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center hover:bg-black/10 dark:hover:bg-white/20 transition-all active:scale-90 cursor-pointer"
                title="Close Modal"
              >
                <mat-icon class="!text-[18px]">close</mat-icon>
              </button>
            </div>

            <!-- Modal Body Form -->
            <form
              [formGroup]="complaintForm"
              (ngSubmit)="submitComplaint()"
              class="p-6 space-y-5 overflow-y-auto flex-1"
            >
              <!-- Submitter Person Card -->
              <div class="space-y-1.5">
                <span class="text-[10px] font-black uppercase tracking-widest opacity-40 px-1">
                  Authenticated Account
                </span>
                <div class="rounded-2xl bg-black/[0.03] dark:bg-white/[0.03] border border-black/5 dark:border-white/10 p-1">
                  <app-person-card [account]="authenticatedPersonCard()"></app-person-card>
                </div>
              </div>

              <!-- Subject Input -->
              <div class="space-y-1.5">
                <label for="complaint-subject" class="text-[10px] font-black uppercase tracking-widest opacity-40 px-1">
                  Complaint Subject *
                </label>
                <div class="ios-input-container !h-12">
                  <mat-icon class="text-ios-blue mr-2.5 !text-[18px]">title</mat-icon>
                  <input
                    id="complaint-subject"
                    type="text"
                    formControlName="subject"
                    placeholder="Brief summary of the issue..."
                    class="ios-input !text-sm"
                  />
                </div>
              </div>

              <!-- Category & Priority Row -->
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div class="space-y-1.5">
                  <label for="complaint-category" class="text-[10px] font-black uppercase tracking-widest opacity-40 px-1">
                    Category *
                  </label>
                  <div class="ios-input-container !h-12">
                    <mat-icon class="text-ios-purple mr-2 !text-[18px]">folder_open</mat-icon>
                    <select
                      id="complaint-category"
                      formControlName="category"
                      class="ios-input !text-sm bg-transparent cursor-pointer lowercase"
                    >
                      <option value="account" class="bg-white dark:bg-zinc-900">account</option>
                      <option value="verification" class="bg-white dark:bg-zinc-900">verification</option>
                      <option value="payments" class="bg-white dark:bg-zinc-900">payments</option>
                      <option value="technical" class="bg-white dark:bg-zinc-900">technical</option>
                      <option value="privacy" class="bg-white dark:bg-zinc-900">privacy</option>
                      <option value="safety" class="bg-white dark:bg-zinc-900">safety</option>
                      <option value="other" class="bg-white dark:bg-zinc-900">other</option>
                    </select>
                  </div>
                </div>

                <div class="space-y-1.5">
                  <label for="complaint-priority" class="text-[10px] font-black uppercase tracking-widest opacity-40 px-1">
                    Priority *
                  </label>
                  <div class="ios-input-container !h-12">
                    <mat-icon class="text-amber-500 mr-2 !text-[18px]">flag</mat-icon>
                    <select
                      id="complaint-priority"
                      formControlName="priority"
                      class="ios-input !text-sm bg-transparent cursor-pointer lowercase"
                    >
                      <option value="low" class="bg-white dark:bg-zinc-900">low</option>
                      <option value="medium" class="bg-white dark:bg-zinc-900">medium</option>
                      <option value="high" class="bg-white dark:bg-zinc-900">high</option>
                      <option value="urgent" class="bg-white dark:bg-zinc-900">urgent</option>
                    </select>
                  </div>
                </div>
              </div>

              <!-- Detailed Description -->
              <div class="space-y-1.5">
                <label for="complaint-desc" class="text-[10px] font-black uppercase tracking-widest opacity-40 px-1">
                  Detailed Description *
                </label>
                <div class="rounded-2xl bg-light-surface dark:bg-dark-surface border border-light-border dark:border-dark-border p-4 focus-within:ring-2 focus-within:ring-ios-blue/50 transition-all">
                  <textarea
                    id="complaint-desc"
                    formControlName="description"
                    rows="4"
                    placeholder="Provide complete details, steps to reproduce, or transaction references..."
                    class="w-full bg-transparent border-none outline-none text-sm resize-none"
                  ></textarea>
                </div>
              </div>

              <!-- Optional Screenshot / Evidence Upload -->
              <div class="space-y-1.5">
                <div class="flex items-center justify-between px-1">
                  <label for="complaint-attachment" class="text-[10px] font-black uppercase tracking-widest opacity-40">
                    Supporting Screenshot (Optional)
                  </label>
                  @if (attachmentPreviewUrl()) {
                    <button
                      type="button"
                      (click)="clearAttachment()"
                      class="text-[11px] font-bold text-ios-red hover:underline cursor-pointer"
                    >
                      Remove
                    </button>
                  }
                </div>

                @if (attachmentPreviewUrl()) {
                  <div class="relative rounded-2xl overflow-hidden border border-black/10 dark:border-white/10 aspect-video bg-black/5">
                    <img
                      [src]="attachmentPreviewUrl()"
                      alt="Attachment preview"
                      class="w-full h-full object-cover"
                      referrerpolicy="no-referrer"
                    />
                  </div>
                } @else {
                  <label
                    for="complaint-attachment"
                    class="flex items-center justify-center gap-2.5 p-4 rounded-2xl border-2 border-dashed border-ios-blue/30 hover:border-ios-blue bg-ios-blue/5 cursor-pointer transition-all"
                  >
                    <mat-icon class="text-ios-blue">add_photo_alternate</mat-icon>
                    <span class="text-xs font-bold text-ios-blue">Attach screenshot or image evidence</span>
                    <input
                      id="complaint-attachment"
                      type="file"
                      accept="image/*"
                      class="hidden"
                      (change)="onAttachmentSelected($event)"
                    />
                  </label>
                }
              </div>

              <!-- Submit Action -->
              <div class="pt-2 flex items-center justify-end gap-3">
                <button
                  type="button"
                  (click)="closeCreateModal()"
                  class="h-12 px-5 rounded-2xl bg-black/5 dark:bg-white/10 font-bold text-xs sm:text-sm hover:bg-black/10 dark:hover:bg-white/15 transition-all cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  [disabled]="complaintForm.invalid || isSubmitting()"
                  class="h-12 px-6 rounded-2xl bg-ios-blue text-white font-bold text-xs sm:text-sm shadow-ios shadow-ios-blue/25 hover:scale-[1.02] active:scale-95 transition-all disabled:opacity-40 flex items-center gap-2 cursor-pointer"
                >
                  @if (isSubmitting()) {
                    <div class="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  } @else {
                    <mat-icon class="!text-[18px]">send</mat-icon>
                  }
                  <span>Submit Complaint</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      }

      <!-- ========================================================================= -->
      <!-- MODAL 2: COMPLAINT DETAIL & FOLLOW-UP DRAWER                              -->
      <!-- ========================================================================= -->
      @if (selectedComplaint(); as activeComplaint) {
        <div class="fixed inset-0 z-[1000] flex items-center justify-center p-4 animate-ios-fade">
          <div
            class="absolute inset-0 bg-black/60 backdrop-blur-md"
            (click)="closeDetailModal()"
            (keydown.enter)="closeDetailModal()"
            tabindex="0"
          ></div>

          <div class="relative w-full max-w-xl bg-white/95 dark:bg-zinc-900/95 backdrop-blur-3xl rounded-[36px] shadow-2xl border border-black/5 dark:border-white/10 animate-ios-slide overflow-hidden flex flex-col max-h-[90vh]">
            <!-- Header -->
            <div class="p-6 border-b border-black/5 dark:border-white/10 flex items-center justify-between gap-4">
              <div class="min-w-0">
                <div class="flex items-center gap-2 mb-1">
                  <span class="px-2.5 py-0.5 rounded-lg bg-black/5 dark:bg-white/10 text-[11px] font-mono font-bold">
                    {{ activeComplaint.ticketNumber }}
                  </span>
                  <span
                    class="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest"
                    [class.bg-amber-500/15]="activeComplaint.status === 'pending'"
                    [class.text-amber-500]="activeComplaint.status === 'pending'"
                    [class.bg-emerald-500/15]="activeComplaint.status === 'closed'"
                    [class.text-emerald-500]="activeComplaint.status === 'closed'"
                  >
                    {{ activeComplaint.status }}
                  </span>
                </div>
                <h3 class="text-lg font-bold tracking-tight truncate">{{ activeComplaint.subject }}</h3>
              </div>
              <button
                type="button"
                (click)="closeDetailModal()"
                class="w-9 h-9 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center hover:bg-black/10 transition-all shrink-0 cursor-pointer"
                title="Close Details"
              >
                <mat-icon class="!text-[18px]">close</mat-icon>
              </button>
            </div>

            <!-- Body -->
            <div class="p-6 space-y-6 overflow-y-auto flex-1">
              <!-- Metadata Pills -->
              <div class="flex flex-wrap items-center gap-2 text-xs">
                <span class="px-3 py-1 rounded-xl bg-ios-blue/10 text-ios-blue font-bold uppercase text-[10px] tracking-wider">
                  Category: {{ activeComplaint.category }}
                </span>
                <span class="px-3 py-1 rounded-xl bg-ios-purple/10 text-ios-purple font-bold uppercase text-[10px] tracking-wider">
                  Priority: {{ activeComplaint.priority }}
                </span>
                <span class="px-3 py-1 rounded-xl bg-black/5 dark:bg-white/5 font-medium text-[11px] opacity-70">
                  Created: {{ toDate(activeComplaint.createdAt) | date: 'MMM d, y, h:mm a' }}
                </span>
              </div>

              <!-- Description Box -->
              <div class="p-5 rounded-3xl bg-black/[0.03] dark:bg-white/[0.03] border border-black/5 dark:border-white/10">
                <p class="text-sm leading-relaxed whitespace-pre-line">{{ activeComplaint.description }}</p>
              </div>

              @if (activeComplaint.attachmentUrl) {
                <div class="rounded-3xl overflow-hidden border border-black/10 dark:border-white/10">
                  <img
                    [src]="activeComplaint.attachmentUrl"
                    alt="Complaint Evidence"
                    class="w-full max-h-72 object-cover"
                    referrerpolicy="no-referrer"
                  />
                </div>
              }

              <!-- Resolution / Follow-Up Note Input -->
              <div class="space-y-3">
                <label for="resolution-note-input" class="text-[10px] font-black uppercase tracking-widest opacity-40 px-1">
                  Resolution / Follow-up Note
                </label>
                <div class="flex gap-2">
                  <div class="ios-input-container !h-12 flex-1">
                    <input
                      id="resolution-note-input"
                      type="text"
                      [formControl]="resolutionNoteControl"
                      placeholder="Add a follow-up remark or resolution summary..."
                      class="ios-input !text-sm"
                    />
                  </div>
                  <button
                    type="button"
                    (click)="saveResolutionNote(activeComplaint)"
                    [disabled]="!resolutionNoteControl.value.trim()"
                    class="h-12 px-4 rounded-2xl bg-ios-blue text-white font-bold text-xs shadow-sm disabled:opacity-40 cursor-pointer flex items-center gap-1.5"
                  >
                    <mat-icon class="!text-[16px]">save</mat-icon>
                    <span>Save</span>
                  </button>
                </div>
              </div>

              @if (activeComplaint.adminReply) {
                <div class="p-4 rounded-2xl bg-emerald-500/10 border border-emerald-500/20 space-y-1">
                  <div class="flex items-center justify-between text-[10px] font-black uppercase tracking-widest text-emerald-600 dark:text-emerald-400">
                    <span class="flex items-center gap-1">
                      <mat-icon class="!text-[14px]">verified_user</mat-icon>
                      Official Admin Response
                    </span>
                    @if (activeComplaint.adminRepliedAt) {
                      <span>{{ toDate(activeComplaint.adminRepliedAt) | date: 'MMM d, h:mm a' }}</span>
                    }
                  </div>
                  <p class="text-sm font-medium whitespace-pre-line">{{ activeComplaint.adminReply }}</p>
                </div>
              }

              <!-- Activity Updates Log -->
              @if (complaintUpdates().length > 0) {
                <div class="space-y-2.5">
                  <span class="text-[10px] font-black uppercase tracking-widest opacity-40 px-1">
                    Activity & Replies Log
                  </span>
                  <div class="space-y-2">
                    @for (upd of complaintUpdates(); track upd.id) {
                      <div
                        class="p-3.5 rounded-2xl border text-xs"
                        [class.bg-ios-blue/10]="upd.authorRole === 'admin'"
                        [class.border-ios-blue/20]="upd.authorRole === 'admin'"
                        [class.bg-black/[0.03]]="upd.authorRole !== 'admin'"
                        [class.dark:bg-white/[0.04]]="upd.authorRole !== 'admin'"
                        [class.border-black/5]="upd.authorRole !== 'admin'"
                        [class.dark:border-white/5]="upd.authorRole !== 'admin'"
                      >
                        <div class="flex items-center justify-between text-[10px] font-bold mb-1">
                          <span class="flex items-center gap-1.5" [class.text-ios-blue]="upd.authorRole === 'admin'" [class.opacity-60]="upd.authorRole !== 'admin'">
                            @if (upd.authorRole === 'admin') {
                              <mat-icon class="!text-[13px]">admin_panel_settings</mat-icon>
                            }
                            {{ upd.authorName }}
                          </span>
                          <span class="opacity-50">{{ toDate(upd.createdAt) | date: 'MMM d, h:mm a' }}</span>
                        </div>
                        <p class="font-medium whitespace-pre-line">{{ upd.message }}</p>
                      </div>
                    }
                  </div>
                </div>
              }
            </div>

            <!-- Footer Actions -->
            <div class="p-5 border-t border-black/5 dark:border-white/10 flex items-center justify-between gap-3">
              <button
                type="button"
                (click)="deleteComplaint(activeComplaint)"
                class="h-11 px-4 rounded-2xl bg-ios-red/10 text-ios-red hover:bg-ios-red hover:text-white font-bold text-xs transition-all flex items-center gap-1.5 cursor-pointer"
              >
                <mat-icon class="!text-[16px]">delete</mat-icon>
                <span>Delete</span>
              </button>

              <button
                type="button"
                (click)="toggleComplaintStatus(activeComplaint)"
                class="h-11 px-5 rounded-2xl font-bold text-xs text-white transition-all flex items-center gap-2 shadow-md cursor-pointer"
                [class.bg-emerald-500]="activeComplaint.status === 'pending'"
                [class.bg-amber-500]="activeComplaint.status === 'closed'"
              >
                <mat-icon class="!text-[18px]">
                  {{ activeComplaint.status === 'pending' ? 'task_alt' : 'replay' }}
                </mat-icon>
                <span>
                  {{ activeComplaint.status === 'pending' ? 'Mark as Closed' : 'Reopen as Pending' }}
                </span>
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  `,
})
export class EverView implements OnDestroy {
  authService = inject(AuthService);
  private notification = inject(NotificationService);
  private platformId = inject(PLATFORM_ID);

  // Chip tabs strictly labeled 'All', 'Pending', and 'Closed'
  readonly chips: { label: ComplaintTab; icon: string }[] = [
    { label: 'All', icon: 'all_inbox' },
    { label: 'Pending', icon: 'pending_actions' },
    { label: 'Closed', icon: 'task_alt' },
  ];

  selectedTab = signal<ComplaintTab>('All');
  complaints = signal<EverComplaint[]>([]);
  complaintUpdates = signal<EverComplaintUpdate[]>([]);
  isLoadingComplaints = signal<boolean>(false);

  // Modals & Selection
  isCreateModalOpen = signal<boolean>(false);
  isSubmitting = signal<boolean>(false);
  selectedComplaint = signal<EverComplaint | null>(null);

  // Attachment upload state
  selectedAttachmentFile = signal<File | null>(null);
  attachmentPreviewUrl = signal<string | null>(null);

  // Search & Filter controls
  complaintSearchControl = new FormControl<string>('', { nonNullable: true });
  categoryFilterControl = new FormControl<string>('all', { nonNullable: true });
  faqSearchControl = new FormControl<string>('', { nonNullable: true });
  resolutionNoteControl = new FormControl<string>('', { nonNullable: true });

  private searchQuerySignal = signal<string>('');
  private categoryFilterSignal = signal<string>('all');
  private faqQuerySignal = signal<string>('');

  // Reactive Form for New Complaint
  complaintForm = new FormGroup({
    subject: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(200)],
    }),
    category: new FormControl<ComplaintCategory>('technical', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    priority: new FormControl<ComplaintPriority>('medium', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    description: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(5000)],
    }),
  });

  // Logged-out FAQs
  faqs = signal([
    {
      question: 'How do I search on Kicgle?',
      answer:
        'Simply type your query into the search bar and press enter. You can also use voice search or image search for more specific results.',
    },
    {
      question: 'Is my search history private?',
      answer:
        'Yes, your search history is private and only accessible to you. You can clear it at any time in your account settings.',
    },
    {
      question: 'How do I change my theme?',
      answer:
        'You can toggle between light and dark modes using the sun/moon icon in the top right corner of any page.',
    },
    {
      question: 'How do I verify my email?',
      answer:
        'Go to your account settings and click on "Verify Email Address". We will send a verification link to your registered email.',
    },
  ]);

  filteredFaqs = computed(() => {
    const q = this.faqQuerySignal().trim().toLowerCase();
    if (!q) return this.faqs();
    return this.faqs().filter(
      (f) =>
        f.question.toLowerCase().includes(q) ||
        f.answer.toLowerCase().includes(q)
    );
  });

  // Counts for 'all', 'pending', 'closed'
  allCount = computed(() => this.complaints().length);
  pendingCount = computed(
    () => this.complaints().filter((c) => c.status === 'pending').length
  );
  closedCount = computed(
    () => this.complaints().filter((c) => c.status === 'closed').length
  );

  // Filtered complaints based on selected chip ('All', 'Pending', 'Closed'), category, and search query
  filteredComplaints = computed(() => {
    const tab = this.selectedTab().toLowerCase();
    const cat = this.categoryFilterSignal();
    const q = this.searchQuerySignal().trim().toLowerCase();

    return this.complaints().filter((item) => {
      if (tab === 'pending' && item.status !== 'pending') return false;
      if (tab === 'closed' && item.status !== 'closed') return false;
      if (cat !== 'all' && item.category !== cat) return false;
      if (q) {
        const hay =
          `${item.ticketNumber} ${item.subject} ${item.description} ${item.category}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  });

  // Authenticated user's Person Card profile
  authenticatedPersonCard = computed<AuthProfile>(() => {
    const profile = this.authService.userProfile();
    const user = this.authService.user();
    return {
      uid: user?.uid || profile?.uid || '',
      email: user?.email || profile?.email || '',
      displayName: profile?.displayName || user?.displayName || 'User',
      username:
        profile?.username ||
        (user?.email ? user.email.split('@')[0] : 'user'),
      photoURL: profile?.photoURL || user?.photoURL || '',
      accountType: profile?.accountType || 'person',
      status: profile?.status || AccountStatus.ACTIVE,
      emailVerified: !!user?.emailVerified,
      isOnline: true,
    };
  });

  private everUnsub: Unsubscribe | null = null;
  private updatesUnsub: Unsubscribe | null = null;

  constructor() {
    this.complaintSearchControl.valueChanges.subscribe((val) =>
      this.searchQuerySignal.set(val || '')
    );
    this.categoryFilterControl.valueChanges.subscribe((val) =>
      this.categoryFilterSignal.set(val || 'all')
    );
    this.faqSearchControl.valueChanges.subscribe((val) =>
      this.faqQuerySignal.set(val || '')
    );

    // Subscribe to Firestore `accounts/{uid}/ever` when user is logged in
    effect(() => {
      if (!isPlatformBrowser(this.platformId)) return;
      const user = this.authService.user();
      this.cleanupListeners();

      if (user?.uid) {
        this.listenToUserComplaints(user.uid);
      } else {
        this.complaints.set([]);
      }
    });
  }

  ngOnDestroy(): void {
    this.cleanupListeners();
  }

  private cleanupListeners(): void {
    if (this.everUnsub) {
      this.everUnsub();
      this.everUnsub = null;
    }
    if (this.updatesUnsub) {
      this.updatesUnsub();
      this.updatesUnsub = null;
    }
  }

  private listenToUserComplaints(uid: string): void {
    this.isLoadingComplaints.set(true);
    // Strictly lowercase collection path: accounts/{uid}/ever
    const everPath = `accounts/${uid}/ever`;
    const everRef = collection(db, 'accounts', uid, 'ever');
    const q = query(everRef, orderBy('createdAt', 'desc'));

    this.everUnsub = onSnapshot(
      q,
      (snapshot) => {
        const list: EverComplaint[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            uid: data['uid'] || uid,
            ticketNumber:
              data['ticketNumber'] ||
              `#ever-${docSnap.id.slice(0, 6).toLowerCase()}`,
            subject: data['subject'] || 'Untitled Complaint',
            category: (data['category'] as ComplaintCategory) || 'other',
            priority: (data['priority'] as ComplaintPriority) || 'medium',
            description: data['description'] || '',
            status:
              data['status'] === 'closed' ? 'closed' : ('pending' as ComplaintStatus),
            attachmentUrl: data['attachmentUrl'] || undefined,
            resolutionNote: data['resolutionNote'] || undefined,
            adminReply: data['adminReply'] || undefined,
            adminRepliedBy: data['adminRepliedBy'] || undefined,
            adminRepliedAt: data['adminRepliedAt'] || null,
            userDisplayName: data['userDisplayName'] || undefined,
            userUsername: data['userUsername'] || undefined,
            userPhotoURL: data['userPhotoURL'] || undefined,
            createdAt: data['createdAt'] || new Date().toISOString(),
            updatedAt: data['updatedAt'] || new Date().toISOString(),
            closedAt: data['closedAt'] || null,
          };
        });
        this.complaints.set(list);
        this.isLoadingComplaints.set(false);

        // Keep selectedComplaint in sync if open
        const currentSelected = this.selectedComplaint();
        if (currentSelected) {
          const updated = list.find((c) => c.id === currentSelected.id);
          if (updated) {
            this.selectedComplaint.set(updated);
          }
        }
      },
      (error) => {
        this.isLoadingComplaints.set(false);
        handleFirestoreError(error, OperationType.LIST, everPath);
      }
    );
  }

  selectTab(tab: ComplaintTab | string): void {
    const t = (tab || '').toLowerCase();
    if (t === 'pending') {
      this.selectedTab.set('Pending');
    } else if (t === 'closed') {
      this.selectedTab.set('Closed');
    } else {
      this.selectedTab.set('All');
    }
  }

  onTabSwipe(tabLabel: string): void {
    const t = (tabLabel || '').toLowerCase();
    if (t === 'all' || t === 'pending' || t === 'closed') {
      this.selectTab(tabLabel);
    }
  }

  getCountForTab(tab: ComplaintTab | string): number {
    const t = (tab || '').toLowerCase();
    if (t === 'pending') return this.pendingCount();
    if (t === 'closed') return this.closedCount();
    return this.allCount();
  }

  openCreateModal(): void {
    this.complaintForm.reset({
      subject: '',
      category: 'technical',
      priority: 'medium',
      description: '',
    });
    this.clearAttachment();
    this.isCreateModalOpen.set(true);
  }

  closeCreateModal(): void {
    this.isCreateModalOpen.set(false);
  }

  onAttachmentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.selectedAttachmentFile.set(file);
    const reader = new FileReader();
    reader.onload = () => {
      this.attachmentPreviewUrl.set(reader.result as string);
    };
    reader.readAsDataURL(file);
  }

  clearAttachment(): void {
    this.selectedAttachmentFile.set(null);
    this.attachmentPreviewUrl.set(null);
  }

  async submitComplaint(): Promise<void> {
    if (this.complaintForm.invalid || this.isSubmitting()) return;
    const user = this.authService.user();
    if (!user?.uid) return;

    this.isSubmitting.set(true);
    const everPath = `accounts/${user.uid}/ever`;

    try {
      let attachmentUrl = '';
      const file = this.selectedAttachmentFile();
      if (file) {
        const timestamp = Date.now();
        const storagePath = `accounts/${user.uid}/ever/${timestamp}_${file.name}`;
        const storageRef = ref(storage, storagePath);
        await uploadBytes(storageRef, file);
        attachmentUrl = await getDownloadURL(storageRef);
      }

      const raw = this.complaintForm.getRawValue();
      const ticketNumber = `#ever-${Math.floor(100000 + Math.random() * 900000)}`;
      const person = this.authenticatedPersonCard();

      const payload: Record<string, unknown> = {
        uid: user.uid,
        ticketNumber,
        subject: raw.subject.trim(),
        category: raw.category,
        priority: raw.priority,
        description: raw.description.trim(),
        status: 'pending',
        attachmentUrl,
        userDisplayName: person.displayName || 'User',
        userUsername: person.username || 'user',
        userPhotoURL: person.photoURL || '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      await addDoc(collection(db, 'accounts', user.uid, 'ever'), payload);
      this.notification.show('Complaint submitted successfully.', 'success');
      this.closeCreateModal();
    } catch (error) {
      handleFirestoreError(error, OperationType.CREATE, everPath);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  openDetailModal(item: EverComplaint): void {
    this.selectedComplaint.set(item);
    this.resolutionNoteControl.setValue(item.resolutionNote || '');
    this.listenToComplaintUpdates(item);
  }

  closeDetailModal(): void {
    this.selectedComplaint.set(null);
    this.complaintUpdates.set([]);
    if (this.updatesUnsub) {
      this.updatesUnsub();
      this.updatesUnsub = null;
    }
  }

  private listenToComplaintUpdates(item: EverComplaint): void {
    if (this.updatesUnsub) {
      this.updatesUnsub();
      this.updatesUnsub = null;
    }
    const updatesPath = `accounts/${item.uid}/ever/${item.id}/updates`;
    const updatesRef = collection(
      db,
      'accounts',
      item.uid,
      'ever',
      item.id,
      'updates'
    );
    const q = query(updatesRef, orderBy('createdAt', 'desc'));

    this.updatesUnsub = onSnapshot(
      q,
      (snap) => {
        const list: EverComplaintUpdate[] = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            uid: data['uid'] || item.uid,
            authorName: data['authorName'] || 'User',
            authorRole: data['authorRole'] === 'admin' ? 'admin' : 'user',
            message: data['message'] || '',
            createdAt: data['createdAt'] || new Date().toISOString(),
          };
        });
        this.complaintUpdates.set(list);
      },
      (error) => {
        handleFirestoreError(error, OperationType.LIST, updatesPath);
      }
    );
  }

  async toggleComplaintStatus(item: EverComplaint): Promise<void> {
    const user = this.authService.user();
    if (!user?.uid) return;
    const nextStatus: ComplaintStatus =
      item.status === 'pending' ? 'closed' : 'pending';
    const docPath = `accounts/${user.uid}/ever/${item.id}`;

    try {
      await updateDoc(doc(db, 'accounts', user.uid, 'ever', item.id), {
        status: nextStatus,
        updatedAt: serverTimestamp(),
        closedAt: nextStatus === 'closed' ? serverTimestamp() : null,
      });
      this.notification.show(
        `Complaint marked as ${nextStatus}.`,
        'success'
      );
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, docPath);
    }
  }

  async saveResolutionNote(item: EverComplaint): Promise<void> {
    const user = this.authService.user();
    const note = this.resolutionNoteControl.value.trim();
    if (!user?.uid || !note) return;

    const docPath = `accounts/${user.uid}/ever/${item.id}`;
    const updatesPath = `accounts/${user.uid}/ever/${item.id}/updates`;

    try {
      await updateDoc(doc(db, 'accounts', user.uid, 'ever', item.id), {
        resolutionNote: note,
        updatedAt: serverTimestamp(),
      });
      await addDoc(
        collection(db, 'accounts', user.uid, 'ever', item.id, 'updates'),
        {
          uid: user.uid,
          authorName:
            this.authService.userProfile()?.displayName || 'Account Holder',
          authorRole: 'user',
          message: note,
          createdAt: serverTimestamp(),
        }
      );
      this.notification.show('Follow-up note saved.', 'success');
    } catch (error) {
      handleFirestoreError(error, OperationType.WRITE, updatesPath || docPath);
    }
  }

  async deleteComplaint(item: EverComplaint): Promise<void> {
    const user = this.authService.user();
    if (!user?.uid) return;
    const docPath = `accounts/${user.uid}/ever/${item.id}`;

    try {
      await deleteDoc(doc(db, 'accounts', user.uid, 'ever', item.id));
      if (this.selectedComplaint()?.id === item.id) {
        this.closeDetailModal();
      }
      this.notification.show('Complaint deleted.', 'info');
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, docPath);
    }
  }

  toDate(val: unknown): Date {
    if (!val) return new Date();
    if (val instanceof Date) return val;
    if (typeof (val as Timestamp)?.toDate === 'function') {
      return (val as Timestamp).toDate();
    }
    if (typeof val === 'string' || typeof val === 'number') {
      const parsed = new Date(val);
      return isNaN(parsed.getTime()) ? new Date() : parsed;
    }
    return new Date();
  }
}

export default EverView;
